import { getCurrentSystem, lang } from '@umu/ukit-lang';
import { useCallback, useEffect, useState, useRef } from 'react';
import { getCertificateTemplate } from '../dataFlow/svgEditor';
import { CertificateReceiveStatus } from '../interface/api';
import { isInApp, isInMobile } from '@umu/umu-ua';
import { openWindow } from '@umu/umu-dom';
import { Modal } from '@umu/umu-design';
import SvgEditor from '../utils/svgEditor';
import { onTransformCertificateDate } from '../utils';
import { useSvgLinkEvents } from './useSvgLinkEvents';
import isEqual from 'lodash/isEqual';
import { IWatermarkConfig } from '../interface';

const { confirm } = Modal;
interface IUseCertificateSvgProps {
    templateSrc?: string;
    config: Record<string, string>;
    status?: CertificateReceiveStatus;
    watermark?: boolean;
    watermarkConfig?: IWatermarkConfig;
}
interface IUseCertificateSvgData {
    svgUrl: string;
    content: string;
}
interface IUseCertificateSvgReturn extends IUseCertificateSvgData {
    isLoading: boolean;
    isDisabled: boolean;
    grayscaleClass: string;
    updateSvg: (newConfig: IUseCertificateSvgProps['config']) => Promise<void>;
    clearSvg: () => void;
    bindLinkEvents: (element: HTMLElement) => void;
}

const defaultData: IUseCertificateSvgData = {
    svgUrl: '',
    content: '',
};
/**
 * 证书 SVG Hook
 * 功能：根据配置生成证书 SVG 链接
 */
const useCertificateSvg = (info: IUseCertificateSvgProps): IUseCertificateSvgReturn => {
    const { config, templateSrc = '', status, watermark = false, watermarkConfig } = info;
    const isInAppOrMobile = isInApp() || isInMobile();
    const refCatchConfig = useRef<IUseCertificateSvgProps['config'] | null>(null);
    const [data, setData] = useState<IUseCertificateSvgData>({ ...defaultData });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { bindLinkEvents } = useSvgLinkEvents({
        allowOpenWindow: status !== CertificateReceiveStatus.NOT_RECEIVED,
        onLinkClick: ({ url }) => {
            if (isInAppOrMobile) {
                confirm({
                    title: lang('提示'),
                    content: lang('请在电脑端打开该链接。'),
                    okText: lang('确认'),
                    centered: true,
                });
                return;
            }
            openWindow(url, '_blank');
        },
    }); // 使用链接事件监听 Hook

    // 状态控制
    const isDisabled = status === CertificateReceiveStatus.NOT_RECEIVED;
    const getTemplateContent = useCallback(async (): Promise<string> => {
        if (templateSrc) {
            try {
                const response = await fetch(templateSrc);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return await response.text();
            } catch (_err) {
                return '';
            }
        }
        return await getCertificateTemplate(getCurrentSystem() || 'default');
    }, [templateSrc]);
    // 更新 SVG
    const updateSvg = useCallback(
        async (newConfig: IUseCertificateSvgProps['config']): Promise<void> => {
            try {
                const { link = '', date = '', qr = '', ...rest } = newConfig;
                if (!rest.title) {
                    return;
                }
                setIsLoading(true);
                // 获取证书模板
                const template = await getTemplateContent();

                // 创建 SVG 编辑器实例
                const svgEditor = new SvgEditor(template);

                // 应用配置
                svgEditor.setQr(qr);
                if (date) {
                    svgEditor.setDate(onTransformCertificateDate(date));
                } else {
                    // else 分支处理
                }
                svgEditor.setLink(link);
                svgEditor.setBatchContent(rest);

                // 应用水印配置
                if (watermark) {
                    // 使用默认值或用户配置
                    const defaultWatermarkConfig = {
                        text: lang('此为样例'),
                        color: '#4A93FF',
                        size: 6,
                        trasparency: 0.2,
                        rotate: 30,
                        rows: 4,
                        splitCount: 6,
                    };
                    const finalWatermarkConfig = watermarkConfig
                        ? {
                              ...defaultWatermarkConfig,
                              ...watermarkConfig,
                          }
                        : defaultWatermarkConfig;
                    svgEditor.setWatermark(finalWatermarkConfig);
                } else {
                    // else 分支处理
                }
                // 生成 SVG 内容
                const svgContent = svgEditor.getSvg();

                // 转换为 Blob URL
                const blob = new Blob([svgContent], { type: 'image/svg+xml' });
                const blobUrl = URL.createObjectURL(blob);

                // 清理旧的 URL
                setData((prevData) => ({
                    ...prevData,
                    svgUrl: blobUrl,
                    content: svgContent,
                }));
            } catch (_err) {
            } finally {
                setIsLoading(false);
            }
        },
        [watermark, watermarkConfig, getTemplateContent],
    ); // 添加依赖

    // 清理 SVG
    const clearSvg = useCallback((): void => {
        if (data.svgUrl) {
            URL.revokeObjectURL(data.svgUrl);
        }
        setData({ ...defaultData });
    }, [data.svgUrl]);
    // 初始化时生成一次 SVG
    useEffect(() => {
        if (isEqual(refCatchConfig.current, config)) {
            return;
        }
        refCatchConfig.current = config;
        updateSvg(config);
        return () => {
            if (data.svgUrl) {
                URL.revokeObjectURL(data.svgUrl);
            }
        };
    }, [config, watermark, watermarkConfig, updateSvg, data.svgUrl]);

    return {
        ...data,
        isLoading,
        isDisabled,
        grayscaleClass: isDisabled ? 'grayscale-100' : '',
        updateSvg,
        bindLinkEvents,
        clearSvg,
    };
};

export { useCertificateSvg };
